import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { HonestTimePickerInput } from 'components/HonestTimePicker/HonestTimePickerInput'
import { Input } from 'components/Input/Input'
import { NavBar } from 'components/NavBar/NavBar'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Button, Flex, H1WithBack, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { absenceList, DEFAULT_SHIFT_COLORS, getShiftValidationError } from './shiftsData'
import { ISelectValue } from 'constants/globalTypes'
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { BodyText, ParagraphText, TitleText } from 'styling/Typography'
import { ScheduleWeeklyCard, ShiftIconSelectBox, } from './Shifts.styled'
import { BeachIcon } from './Icons/BeachIcon'
import { DrinkIcon } from './Icons/DrinkIcon'
import { HeartIcon } from './Icons/HeartIcon'
import { RotationIcon } from './Icons/RotationIcon'
import { SunriseIcon } from './Icons/SunriseIcon'
import { SunsetIcon } from './Icons/SunsetIcon'
import { convertMinsToHrsMins } from 'utils/helpers'
import { editShift, getSingleShift } from 'features/shifts/shiftsSlice'
import { CancelBtn } from 'components/Button/CancelBtn'
import { toast } from 'react-toastify'
import { SunIcon } from './Icons/SunIcon'
import { tokens } from 'styling/tokens'

export const EditShift = () => {
    const { id } = useParams()
    const [state, setState] = useState<IShiftState>({
        name: '',
        info: '',
        color: DEFAULT_SHIFT_COLORS.color,
        absenceType: {
            value: 6,
            label: 'Active'
        },
        range: {
            shiftStart: 480,
            minutes: 540,
            minutesSelected: false,
            startSelected: false
        },
        icon: 'sunrise',
        iconColor: DEFAULT_SHIFT_COLORS.iconColor,
        textColor: DEFAULT_SHIFT_COLORS.textColor
    })
    const dispatch = useDispatch<AppDispatch>()
    const { singleShift } = useSelector((state: RootState) => state.shifts)
    useFocusFirstInput()
    const navigate = useNavigate()
    const shiftLoaded = Boolean(state.id && String(state.id) === String(id))

    useEffect(() => {
        if (!id) return
        dispatch(getSingleShift(id))
    }, [dispatch, id])

    useEffect(() => {
        if (!id || !singleShift?.id || String(singleShift.id) !== String(id)) return

        setState({
            id: singleShift.id,
            name: singleShift.name ?? '',
            info: singleShift.info ?? '',
            color: '#' + (singleShift.color || 'ffffff').slice(0, 6),
            absenceType: absenceList.find((a) => a.value == (singleShift.absenceType ?? 6)) ?? absenceList[0],
            range: {
                shiftStart: singleShift.shiftStart ?? 0,
                minutes: singleShift.minutes ?? 0,
                minutesSelected: singleShift.shiftStart != null && singleShift.minutes != null,
                startSelected: singleShift.shiftStart != null,
            },
            icon: singleShift.icon || 'sunrise',
            iconColor: '#' + (singleShift.iconColor || 'ffffff').slice(0, 6),
            textColor: '#' + (singleShift.textColor || '000000').slice(0, 6),
        })
    }, [singleShift, id])

    const handleSave = async () => {
        const validationError = getShiftValidationError(state)
        if (validationError) {
            toast.warning(validationError)
            return
        }
        const resp = await dispatch(editShift(state))
        if (resp.meta.requestStatus === 'fulfilled') {
            navigate(-1)
        }
    }

    const handleTimeChange = (time: string, name: string) => {
        const splitted = time.split(':')
        const minutes = parseInt(splitted[0]) * 60 + parseInt(splitted[1])
        if (name == 'shiftStart') {
            setState((prev) => {
                const copy = { ...prev }
                return { ...copy, range: { ...copy.range, shiftStart: minutes, startSelected: true } }
            })
        } else {
            setState((prev) => {
                const copy = { ...prev }
                if (minutes > copy.range.shiftStart) {
                    return { ...copy, range: { ...copy.range, minutes: minutes - copy.range.shiftStart, minutesSelected: true } }
                } else {
                    return { ...copy, range: { ...copy.range, minutes: 1440 - copy.range.shiftStart + minutes, minutesSelected: true } }
                }
            })
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setState(prev => ({ ...prev, [name]: value }))
    }

    const handleSelect = (value: ISelectValue) => {
        setState(prev => ({ ...prev, absenceType: value }))
    }


    const selectIcon = (icon: string) => {
        setState(prev => ({ ...prev, icon }))
    }

    const getIcon = () => {
        switch (state.icon) {
            case 'sunrise':
                return <SunriseIcon stroke={state.iconColor || tokens.accent.base} />
            case 'sunset':
                return <SunsetIcon stroke={state.iconColor || tokens.accent.base} />
            case 'rotation':
                return <RotationIcon stroke={state.iconColor || tokens.accent.base} />
            case 'beach':
                return <BeachIcon stroke={state.iconColor || tokens.accent.base} />
            case 'drink':
                return <DrinkIcon stroke={state.iconColor || tokens.accent.base} />
            case 'heart':
                return <HeartIcon stroke={state.iconColor || tokens.accent.base} />
            case 'sun':
                return <SunIcon stroke={state.iconColor || tokens.accent.base} />
            default:
                return null
        }
    }

    return <>   <NavBar />
        <PageWrapper>
            <H1WithBack arrow title='Edit Shift' />
            <Flex mt='16' gap='2rem'  >
                <Flex column gap='1rem' width='30%' >
                    <TitleText weight='bold' > Shift Details</TitleText>
                    <Input invalid={!state.name} placeholder='Morning shift' labelText='Name' value={state.name} onChange={handleChange} nomarg name='name' />
                    <HonestSelectInput
                        options={absenceList}
                        labelText='Type'
                        nomarg
                        onChange={handleSelect}
                        defaultValue={state.absenceType?.value}
                        invalid={!state.absenceType}
                    />
                    {state.absenceType?.value == 6 && shiftLoaded && <Flex gap='2rem' >
                        <HonestTimePickerInput
                            key={`shift-start-${state.id}-${state.range.shiftStart}`}
                            labelText='From'
                            onChange={(time) => handleTimeChange(time, 'shiftStart')}
                            defaultValue={state.range.shiftStart}
                        />
                        <HonestTimePickerInput
                            key={`shift-end-${state.id}-${state.range.shiftStart}-${state.range.minutes}`}
                            labelText='To'
                            onChange={(time) => handleTimeChange(time, 'minutes')}
                            defaultValue={state.range.minutes}
                            calculated={state.range.shiftStart}
                        />
                    </Flex>}
                    <Input height='150px' placeholder='Enter description' labelText='Shift description (optional)' value={state.info} onChange={handleChange} nomarg name='info' type='textarea' />
                </Flex>
                <Flex column gap='1rem' width='30%' style={{ borderLeft: `1px solid ${tokens.text.muted}`, paddingLeft: '1rem' }} >
                    <TitleText weight='bold' > Shift Design</TitleText>
                    <Flex gap='1rem' >
                        <Input labelText='Card color' value={state.color || tokens.accent.base} onChange={handleChange} nomarg name='color' type='color' />
                        <Input labelText='Icon color' value={state.iconColor || tokens.accent.base} onChange={handleChange} nomarg name='iconColor' type='color' />
                        <Input labelText='Text color' value={state.textColor || tokens.accent.ink} onChange={handleChange} nomarg name='textColor' type='color' />
                    </Flex>
                    <BodyText weight='bold' >Icon</BodyText>
                    <Flex width='100%' gap='1rem' wrap >
                        <ShiftIconSelectBox onClick={() => selectIcon('sunrise')} active={state.icon == 'sunrise'} >
                            <SunriseIcon stroke={state.iconColor || tokens.accent.base} />
                        </ShiftIconSelectBox>
                        <ShiftIconSelectBox onClick={() => selectIcon('sunset')} active={state.icon == 'sunset'} >
                            <SunsetIcon stroke={state.iconColor || tokens.accent.base} />
                        </ShiftIconSelectBox>
                        <ShiftIconSelectBox onClick={() => selectIcon('sun')} active={state.icon == 'sun'} >
                            <SunIcon stroke={state.iconColor || tokens.accent.base} />
                        </ShiftIconSelectBox>
                        <ShiftIconSelectBox onClick={() => selectIcon('rotation')} active={state.icon == 'rotation'} >
                            <RotationIcon stroke={state.iconColor || tokens.accent.base} />
                        </ShiftIconSelectBox>
                        <ShiftIconSelectBox onClick={() => selectIcon('beach')} active={state.icon == 'beach'} >
                            <BeachIcon stroke={state.iconColor || tokens.accent.base} />
                        </ShiftIconSelectBox>
                        <ShiftIconSelectBox onClick={() => selectIcon('drink')} active={state.icon == 'drink'} >
                            <DrinkIcon stroke={state.iconColor || tokens.accent.base} />
                        </ShiftIconSelectBox>
                        <ShiftIconSelectBox onClick={() => selectIcon('heart')} active={state.icon == 'heart'} >
                            <HeartIcon stroke={state.iconColor || tokens.accent.base} />
                        </ShiftIconSelectBox>
                    </Flex>
                    <BodyText weight='bold' >Card Preview</BodyText>
                    <ScheduleWeeklyCard style={{ width: '150px', height: 60 }} color={state.color} >
                        <Flex gap='0.5rem' center >
                            {getIcon()}
                            <Flex column >

                                <BodyText
                                    style={{
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        maxWidth: '100%',
                                        color: state.textColor
                                    }}
                                    weight='bold' >{state.name || 'Shift name'}
                                </BodyText>
                                <ParagraphText style={{ color: state.textColor }} >
                                    {convertMinsToHrsMins(state.range.shiftStart)}-{convertMinsToHrsMins(state.range.minutes, state.range.shiftStart)}
                                </ParagraphText>
                            </Flex>
                        </Flex>
                    </ScheduleWeeklyCard>
                </Flex>
            </Flex>
        </PageWrapper>
        <PageFooter>
            <Flex width='100%' end='true'>
                <CancelBtn />
                <Button ml='15' height='40' orange onClick={handleSave}  >
                    Save
                </Button>
            </Flex>
        </PageFooter>
    </>
}

export interface IShiftState {
    id?: number
    name: string
    info: string
    color?: string
    absenceType: ISelectValue
    range: {
        shiftStart: number
        minutes: number
        minutesSelected: boolean
        startSelected: boolean
    }
    icon: string
    iconColor: string
    textColor: string
} 